import { ArrowLeft, Shield, AlertTriangle, Settings, Edit } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const ComplianceSettings = () => {
  const [settings, setSettings] = useState({
    hotHoldingTemp: { celsius: 63, fahrenheit: 145 },
    coldStorageTemp: { celsius: 4, fahrenheit: 39 },
    automaticAlerts: true,
    requireCorrectiveActions: true,
    managerApproval: true,
    automaticEscalation: false,
    realtimeMonitoring: true,
    dailyReports: true,
    auditTrail: true
  });

  const [editingTemp, setEditingTemp] = useState<'hot' | 'cold' | null>(null);
  const [tempValues, setTempValues] = useState({ celsius: 0, fahrenheit: 0 });

  const openTempEdit = (type: 'hot' | 'cold') => {
    const temp = type === 'hot' ? settings.hotHoldingTemp : settings.coldStorageTemp;
    setTempValues(temp);
    setEditingTemp(type);
  };

  const saveTempSettings = () => {
    if (editingTemp) {
      const newSettings = { ...settings };
      if (editingTemp === 'hot') {
        newSettings.hotHoldingTemp = tempValues;
      } else {
        newSettings.coldStorageTemp = tempValues;
      }
      setSettings(newSettings);
      toast.success(`${editingTemp === 'hot' ? 'Hot holding' : 'Cold storage'} temperature updated`);
      setEditingTemp(null);
    }
  };

  const updateSetting = (key: string, value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    toast.success('Setting updated successfully');
  };

  const resetToDefaults = () => {
    setSettings({
      hotHoldingTemp: { celsius: 63, fahrenheit: 145 },
      coldStorageTemp: { celsius: 4, fahrenheit: 39 },
      automaticAlerts: true,
      requireCorrectiveActions: true,
      managerApproval: true,
      automaticEscalation: false,
      realtimeMonitoring: true,
      dailyReports: true,
      auditTrail: true
    });
    toast.success('Settings reset to defaults');
  };

  const saveAllSettings = () => {
    toast.success('All settings saved successfully');
  };
  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <h1 className="heading-section mb-2">Compliance Settings</h1>
          <p className="text-muted-foreground">Configure compliance rules and monitoring parameters</p>
        </div>

        <div className="space-y-6">
          {/* Temperature Monitoring */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Temperature Monitoring
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Hot Holding Minimum Temperature</h4>
                  <p className="text-sm text-muted-foreground">Minimum safe temperature for hot food holding</p>
                </div>
                <div className="text-right">
                  <div className="font-medium">{settings.hotHoldingTemp.celsius}°C ({settings.hotHoldingTemp.fahrenheit}°F)</div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="glass-button mt-1"
                    onClick={() => openTempEdit('hot')}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Cold Storage Maximum Temperature</h4>
                  <p className="text-sm text-muted-foreground">Maximum safe temperature for cold storage</p>
                </div>
                <div className="text-right">
                  <div className="font-medium">{settings.coldStorageTemp.celsius}°C ({settings.coldStorageTemp.fahrenheit}°F)</div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="glass-button mt-1"
                    onClick={() => openTempEdit('cold')}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Automatic Alerts</h4>
                  <p className="text-sm text-muted-foreground">Send alerts when temperatures are out of range</p>
                </div>
                <Switch 
                  checked={settings.automaticAlerts}
                  onCheckedChange={(checked) => updateSetting('automaticAlerts', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Corrective Actions */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Corrective Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Require Corrective Actions</h4>
                  <p className="text-sm text-muted-foreground">Mandate corrective actions for non-compliance</p>
                </div>
                <Switch 
                  checked={settings.requireCorrectiveActions}
                  onCheckedChange={(checked) => updateSetting('requireCorrectiveActions', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Manager Approval Required</h4>
                  <p className="text-sm text-muted-foreground">Require manager approval for critical actions</p>
                </div>
                <Switch 
                  checked={settings.managerApproval}
                  onCheckedChange={(checked) => updateSetting('managerApproval', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Automatic Escalation</h4>
                  <p className="text-sm text-muted-foreground">Escalate unresolved issues after 24 hours</p>
                </div>
                <Switch 
                  checked={settings.automaticEscalation}
                  onCheckedChange={(checked) => updateSetting('automaticEscalation', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Compliance Monitoring */}
          <Card className="glass-panel border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Compliance Monitoring
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Real-time Monitoring</h4>
                  <p className="text-sm text-muted-foreground">Enable real-time compliance status monitoring</p>
                </div>
                <Switch 
                  checked={settings.realtimeMonitoring}
                  onCheckedChange={(checked) => updateSetting('realtimeMonitoring', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Daily Compliance Reports</h4>
                  <p className="text-sm text-muted-foreground">Generate automatic daily compliance summaries</p>
                </div>
                <Switch 
                  checked={settings.dailyReports}
                  onCheckedChange={(checked) => updateSetting('dailyReports', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Audit Trail Logging</h4>
                  <p className="text-sm text-muted-foreground">Maintain detailed audit trails for all actions</p>
                </div>
                <Switch 
                  checked={settings.auditTrail}
                  onCheckedChange={(checked) => updateSetting('auditTrail', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 flex justify-end gap-4">
          <Button variant="outline" className="glass-button" onClick={resetToDefaults}>
            Reset to Defaults
          </Button>
          <Button className="cta-primary" onClick={saveAllSettings}>
            Save Settings
          </Button>
        </div>

        {/* Temperature Edit Dialog */}
        <Dialog open={!!editingTemp} onOpenChange={() => setEditingTemp(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Edit {editingTemp === 'hot' ? 'Hot Holding' : 'Cold Storage'} Temperature
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="celsius">Celsius</Label>
                  <Input
                    id="celsius"
                    type="number"
                    value={tempValues.celsius}
                    onChange={(e) => setTempValues(prev => ({ 
                      ...prev, 
                      celsius: parseInt(e.target.value) || 0 
                    }))}
                    placeholder="Temperature in °C"
                  />
                </div>
                <div>
                  <Label htmlFor="fahrenheit">Fahrenheit</Label>
                  <Input
                    id="fahrenheit"
                    type="number"
                    value={tempValues.fahrenheit}
                    onChange={(e) => setTempValues(prev => ({ 
                      ...prev, 
                      fahrenheit: parseInt(e.target.value) || 0 
                    }))}
                    placeholder="Temperature in °F"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingTemp(null)}>
                  Cancel
                </Button>
                <Button onClick={saveTempSettings}>
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ComplianceSettings;